import { useCallback, useEffect, useState } from 'react';
import { Eye, PackageCheck, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { getOrder, listOrders, updateOrderStatus } from '../../api/marketplace';
import { getErrorMessage } from '../../api/response';
import Button from '../../components/Button';
import FormField, { inputClassName } from '../../components/FormField';
import Modal from '../../components/Modal';
import PageHeader from '../../components/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import Table from '../../components/Table';
import { LoadingState } from '../../components/PageState';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { currency, date } from '../../utils/format';
import { getStatusConfig, orderStatus } from '../../utils/status';

const statusOptions = [
  { value: '', label: 'Todos los estados' },
  { value: 'pending', label: 'Pendiente de pago' },
  { value: 'paid', label: 'Pagado' },
  { value: 'accepted', label: 'En preparacion' },
  { value: 'ready_for_pickup', label: 'Listo para entregar' },
  { value: 'delivered', label: 'Entregado' },
  { value: 'cancelled', label: 'Cancelado' }
];

const Pedidos = () => {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [nextStatus, setNextStatus] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listOrders({ status: status || undefined, limit: 100 });
      setOrders(data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudieron cargar los pedidos'));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const filteredOrders = orders.filter((order) => {
    const query = debouncedSearch.toLowerCase();
    if (!query) return true;
    return [order.order_number, order.customer_name, order.customer_email, order.customer_phone]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  });

  const pendingDelivery = orders.filter((order) => ['paid', 'ready_for_pickup'].includes(order.status));
  const discountTotal = filteredOrders.reduce((sum, order) => sum + Number(order.discount_amount || 0), 0);

  const openDetail = async (order) => {
    try {
      setSaving(true);
      const detail = await getOrder(order.id);
      setSelected(detail);
      setNextStatus(detail.status);
      setDetailOpen(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo cargar el pedido'));
    } finally {
      setSaving(false);
    }
  };

  const saveStatus = async () => {
    if (!selected || !nextStatus || nextStatus === selected.status) return;

    try {
      setSaving(true);
      const updated = await updateOrderStatus(selected.id, nextStatus);
      setSelected(updated);
      toast.success('Estado de pedido actualizado');
      loadOrders();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo actualizar el estado'));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'order_number',
      label: 'Pedido',
      render: (row) => (
        <div>
          <p className="font-semibold text-gray-900">{row.order_number}</p>
          <p className="text-xs text-gray-500">{date(row.created_at)}</p>
        </div>
      )
    },
    { key: 'customer_name', label: 'Cliente' },
    { key: 'customer_phone', label: 'Telefono', render: (row) => row.customer_phone || '-' },
    {
      key: 'total_amount',
      label: 'Total',
      render: (row) => (
        <div>
          <p className="font-semibold text-gray-900">{currency(row.total_amount)}</p>
          {Number(row.discount_amount || 0) > 0 && (
            <p className="text-xs text-green-700">
              -{currency(row.discount_amount)} descuento
            </p>
          )}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Estado',
      render: (row) => {
        const config = getStatusConfig(orderStatus, row.status);
        return <StatusBadge label={config.label} color={config.color} />;
      }
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <button onClick={() => openDetail(row)} className="rounded-md p-2 text-blue-700 hover:bg-blue-50" title="Ver detalle">
          <Eye className="h-4 w-4" />
        </button>
      )
    }
  ];

  const itemColumns = [
    { key: 'product_name', label: 'Producto' },
    { key: 'quantity', label: 'Cantidad' },
    { key: 'unit_price', label: 'Precio', render: (row) => currency(row.unit_price) },
    {
      key: 'discount_amount',
      label: 'Desc.',
      render: (row) => Number(row.discount_amount || 0) > 0 ? `-${currency(row.discount_amount)}` : '-'
    },
    { key: 'total_amount', label: 'Total', render: (row) => currency(row.total_amount) }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pedidos"
        description="Gestion de pedidos, preparacion, entrega y cancelaciones."
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Pedidos listados</p>
          <p className="mt-1 text-2xl font-bold text-gray-950">{filteredOrders.length}</p>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm text-amber-800">Pagados pendientes</p>
          <p className="mt-1 text-2xl font-bold text-amber-900">{pendingDelivery.length}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Ventas listadas</p>
          <p className="mt-1 text-2xl font-bold text-gray-950">{currency(filteredOrders.reduce((sum, order) => sum + Number(order.total_amount || 0), 0))}</p>
          <p className="mt-1 text-xs text-green-700">Descuentos {currency(discountTotal)}</p>
        </div>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input className={`${inputClassName} pl-9`} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por pedido, cliente o telefono" />
          </div>
          <select className={inputClassName} value={status} onChange={(event) => setStatus(event.target.value)}>
            {statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        {loading ? <div className="p-5"><LoadingState rows={5} /></div> : <Table columns={columns} data={filteredOrders} emptyMessage="No hay pedidos para los filtros seleccionados" />}
      </section>

      <Modal
        open={detailOpen}
        title={selected ? `Pedido ${selected.order_number}` : 'Detalle de pedido'}
        onClose={() => setDetailOpen(false)}
        size="lg"
        footer={
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2">
              <PackageCheck className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">Total {currency(selected?.total_amount)}</span>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={() => setDetailOpen(false)}>Cerrar</Button>
              <Button type="button" onClick={saveStatus} disabled={saving || nextStatus === selected?.status}>
                {saving ? 'Guardando...' : 'Guardar estado'}
              </Button>
            </div>
          </div>
        }
      >
        {selected && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <p className="text-xs font-semibold uppercase text-gray-500">Cliente</p>
                <p className="mt-1 text-sm text-gray-900">{selected.customer_name}</p>
                <p className="text-sm text-gray-600">{selected.customer_phone}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-gray-500">Estado actual</p>
                <div className="mt-2">
                  <StatusBadge {...getStatusConfig(orderStatus, selected.status)} />
                </div>
              </div>
              <div className="rounded-md bg-gray-50 p-3">
                <p className="text-xs font-semibold uppercase text-gray-500">Resumen</p>
                <p className="mt-1 text-sm text-gray-700">Subtotal {currency(selected.subtotal_amount)}</p>
                <p className="text-sm text-green-700">Descuento -{currency(selected.discount_amount)}</p>
                <p className="text-sm font-semibold text-gray-950">Total {currency(selected.total_amount)}</p>
              </div>
              <FormField label="Cambiar estado">
                <select className={inputClassName} value={nextStatus} onChange={(event) => setNextStatus(event.target.value)}>
                  {statusOptions.filter((option) => option.value).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </FormField>
            </div>

            <Table columns={itemColumns} data={selected.items || []} emptyMessage="Pedido sin productos" />

            {selected.notes && (
              <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                {selected.notes}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Pedidos;
